import { isProduction } from '@tink/config/src/env';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { getParameter } from '../../utilities/urlParametersValidator';
import { ProviderEntity } from '../../models/provider';
import { PageView } from '../../models/analytics';

const VIEW_MESSAGE_TYPE = 'view-event';
const ERROR_MESSAGE_TYPE = 'error-event';
const APPLICATION_MESSAGE_TYPE = 'application-event';
const INTERACTION_MESSAGE_TYPE = 'interaction-event';

export enum ParentApplicationEvent {
  INITIALIZED = 'INITIALIZED',
  CREDENTIALS_SUBMITTED = 'CREDENTIALS_SUBMITTED',
  PROVIDER_AUTHENTICATION_INITIALIZED = 'PROVIDER_AUTHENTICATION_INITIALIZED',
  CREDENTIALS_VALIDATION_FAILED = 'CREDENTIALS_VALIDATION_FAILED',
  AUTHENTICATION_SUCCESSFUL = 'AUTHENTICATION_SUCCESSFUL',
  BULK_PAYMENT_SUCCESSFUL = 'BULK_PAYMENT_SUCCESSFUL',
  PAYMENT_SUCCESSFUL = 'PAYMENT_SUCCESSFUL',
  BULK_PAYMENT_INITIALIZED = 'BULK_PAYMENT_INITIALIZED',
  DIRECT_PAYMENT_INITIALIZED = 'DIRECT_PAYMENT_INITIALIZED',
  KYC_SUCCESSFUL = 'KYC_SUCCESSFUL',
  KYC_FAILED = 'KYC_FAILED',
}

enum ParentCredentialsCreatedApplicationEvent {
  CREDENTIALS_CREATED = 'CREDENTIALS_CREATED',
}

type NotifyParent = (message: string) => void;

const postMessageToParent: NotifyParent = (message: string) => {
  const redirectUri = getParameter(TinkLinkUrlParameter.REDIRECT_URI);

  try {
    window.parent.postMessage(message, redirectUri as string);
  } catch (error) {
    // Parent does not match `redirectUri`.
  }
};

export const buildApplicationMessage = (applicationEvent: ParentApplicationEvent) => {
  return JSON.stringify({
    type: APPLICATION_MESSAGE_TYPE,
    data: {
      event: applicationEvent,
    },
  });
};

type CredentialsCreatedApplicationPayload = {
  credentialsId: string;
};

const buildCredentialsCreatedApplicationMessage = ({
  credentialsId,
}: CredentialsCreatedApplicationPayload) => {
  return JSON.stringify({
    type: APPLICATION_MESSAGE_TYPE,
    data: {
      event: ParentCredentialsCreatedApplicationEvent.CREDENTIALS_CREATED,
      credentialsId,
    },
  });
};

type BuildViewMessagePayload = {
  pageView: PageView;
  provider: ProviderEntity | undefined;
  sessionId: string | undefined;
};

const buildViewMessage = ({ pageView, provider, sessionId }: BuildViewMessagePayload) => {
  return JSON.stringify({
    type: VIEW_MESSAGE_TYPE,
    data: {
      screen: pageView,
      sessionId,
      providerName: provider?.name || '',
      providerDisplayName: provider?.displayName || '',
      providerAccessType: provider?.accessType || '',
      providerAuthenticationFlow: provider?.authenticationFlow || '',
    },
  });
};

export enum InteractionElement {
  BACK_BUTTON = 'BACK_BUTTON',
  CLOSE_BUTTON = 'CLOSE_BUTTON',
  PRIMARY_BUTTON = 'PRIMARY_BUTTON',
  SECONDARY_BUTTON = 'SECONDARY_BUTTON',
  INPUT_FIELD = 'INPUT_FIELD',
  EXTERNAL_LINK = 'EXTERNAL_LINK',
  OPEN_BANKID = 'OPEN_BANKID',
}

type BuildInteractionMessagePayload = {
  pageView: PageView;
  element: InteractionElement;
  provider: ProviderEntity | undefined;
  sessionId: string | undefined;
};

const buildInteractionMessage = ({
  pageView,
  element,
  provider,
  sessionId,
}: BuildInteractionMessagePayload) => {
  return JSON.stringify({
    type: INTERACTION_MESSAGE_TYPE,
    data: {
      screen: pageView,
      element,
      sessionId,
      providerName: provider?.name || '',
      providerDisplayName: provider?.displayName || '',
      providerAccessType: provider?.accessType || '',
      providerAuthenticationFlow: provider?.authenticationFlow || '',
    },
  });
};

type BuildErrorMessagePayload = {
  pageView: PageView;
  provider: ProviderEntity | undefined;
  sessionId: string | undefined;
  errorMessage: string;
};

const buildErrorMessage = ({
  pageView,
  errorMessage,
  provider,
  sessionId,
}: BuildErrorMessagePayload) => {
  return JSON.stringify({
    type: ERROR_MESSAGE_TYPE,
    data: {
      screen: pageView,
      sessionId,
      errorMessage,
      providerName: provider?.name || '',
      providerDisplayName: provider?.displayName || '',
      providerAccessType: provider?.accessType || '',
      providerAuthenticationFlow: provider?.authenticationFlow || '',
    },
  });
};

type InteractionEventPayload = {
  pageView: PageView;
  element: InteractionElement;
};

type ErrorEventPayload = {
  pageView: PageView;
  errorMessage: string;
};

export class ParentMessenger {
  provider: ProviderEntity | undefined;

  sessionId: string | undefined;

  isIframe: boolean | undefined;

  isMobileSDK: boolean | undefined;

  isTinkLinkEmbedded: boolean | undefined;

  notifyParent: NotifyParent;

  sentApplicationEvents: ParentApplicationEvent[] = [];

  setIsIframe(isIframe: boolean) {
    this.isIframe = isIframe;
  }

  setIsMobileSDK(isMobileSDK: boolean) {
    this.isMobileSDK = isMobileSDK;
  }

  setIsTinkLinkEmbedded(isTinkLinkEmbedded: boolean) {
    this.isTinkLinkEmbedded = isTinkLinkEmbedded;
  }

  setSessionId(sessionId: string) {
    this.sessionId = sessionId;
  }

  setProvider(provider: ProviderEntity) {
    this.provider = provider;
  }

  constructor(notifyParent: NotifyParent) {
    this.notifyParent = notifyParent;
  }

  propagateMessage(message: string) {
    if (this.isMobileSDK) {
      (window as any).webkit?.messageHandlers?.mobileSDKMessageHandler?.postMessage?.(message);
    }

    if (!this.isTinkLinkEmbedded || !this.isIframe) {
      return;
    }

    this.notifyParent(message);
  }

  fireApplicationEvent(applicationEvent: ParentApplicationEvent) {
    this.propagateMessage(buildApplicationMessage(applicationEvent));
  }

  fireApplicationEventOnce(applicationEvent: ParentApplicationEvent) {
    if (this.sentApplicationEvents.includes(applicationEvent)) {
      return;
    }

    this.sentApplicationEvents.push(applicationEvent);
    this.propagateMessage(buildApplicationMessage(applicationEvent));
  }

  fireCredentialsCreatedEvent(payload: CredentialsCreatedApplicationPayload) {
    this.propagateMessage(buildCredentialsCreatedApplicationMessage(payload));
  }

  fireViewEvent(pageView: PageView) {
    this.propagateMessage(
      buildViewMessage({
        pageView,
        provider: this.provider,
        sessionId: this.sessionId,
      })
    );
  }

  fireInteractionEvent({ pageView, element }: InteractionEventPayload) {
    this.propagateMessage(
      buildInteractionMessage({
        pageView,
        element,
        provider: this.provider,
        sessionId: this.sessionId,
      })
    );
  }

  fireErrorEvent({ pageView, errorMessage }: ErrorEventPayload) {
    this.propagateMessage(
      buildErrorMessage({
        pageView,
        errorMessage,
        provider: this.provider,
        sessionId: this.sessionId,
      })
    );
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const silentJoe: NotifyParent = (message: string) => {
  // Uncomment to debug
  // console.log('PARENT MESSAGE: ', message);
};

const handler = isProduction() ? postMessageToParent : silentJoe;

export const parentMessenger = new ParentMessenger(handler);
